import type { TFunction } from "i18next";
import type { DisabledReason, StepPlan } from "../../../../api/content/stepPlans";
import type { MadeOutput, SwitchWorkflow } from "../../../../api/threads/workflowSwitchApi";
import { kindsText } from "../../../workflows/inputCards/typeWords";
import { valueText } from "../../status/valueText";
import { namesText, reasonText } from "../../stepPlans/planText";

/**
 * What a Workflow a Thread can switch to does with what the Thread has (F126), in words: the
 * outputs it uses, the inputs its first step still misses, and those a later step asks for
 * when it is due (W-7 B). The server's step plans decide all of it; this words it.
 */
export interface SwitchSummary {
  /** The outputs made so far it carries, each named once: "Text from “Write notes”". */
  readonly uses: readonly string[];
  /** Labels of the first step's inputs nothing fills yet: given before switching. */
  readonly missingNow: readonly string[];
  /** Labels of later steps' inputs nothing fills yet: asked at their step. */
  readonly neededLater: readonly string[];
  /** Anything else that keeps it from starting, such as a value that doesn't fit, in words. */
  readonly problems: readonly string[];
}

/** Reuse the Thread catalogue's display names, including earlier Workflows (P-21, RL-2). */
export function withProducerNames(made: readonly MadeOutput[], nameIn: (workflow: string, stepKey: string) => string | null): readonly MadeOutput[] {
  return made.map((output) => ({ ...output, stepName: nameIn(output.workflow, output.stepKey) ?? output.stepName }));
}

/** An output made so far, in words: its kind and the step that made it. */
export function madeText(made: MadeOutput, t: TFunction): string {
  const kind = kindsText([made.kind], t);
  return made.stepName === null ? kind : t("threads.switch.madeBy", { kind, step: made.stepName });
}

/**
 * What the server's preparation says of the Workflow: what it carries, the missing inputs of
 * its first step's plan and of its later steps' plans (the server holds the switch on the
 * first's, ADR-0040 D5), and its other reasons, such as a value that doesn't fit.
 */
export function switchSummary(workflow: SwitchWorkflow, made: readonly MadeOutput[], t: TFunction): SwitchSummary {
  const [first, ...later] = workflow.steps;
  const exposed = new Map(workflow.inputs.map((input) => [input.name, input]));
  const asked = new Map(workflow.asked.map((input) => [input.destination, input]));
  const labelOf = (input: string) => exposed.get(input)?.label ?? asked.get(input)?.label ?? input;
  const missingIn = (plan: StepPlan) => plan.reasons.filter(isMissing).map((reason) => plan.inputs.find((input) => input.name === reason.input)?.label ?? reason.input ?? "");
  const used = [...new Set(workflow.carried.map((carried) => carried.output))];
  return {
    uses: used.flatMap((index) => (made[index] === undefined ? [] : [madeText(made[index], t)])),
    missingNow: first === undefined ? [] : unique(missingIn(first)),
    neededLater: unique(later.flatMap(missingIn)),
    problems: workflow.reasons.filter((reason) => !isMissing(reason)).map((reason) => reasonText(reason, t, labelOf)),
  };
}

/**
 * The summary as the lines under a Workflow's card. The chosen Workflow's card leaves out what
 * is missing now: its inputs show beside it, and the sentence by Switch names what holds it.
 */
export function switchSummaryLines(summary: SwitchSummary, t: TFunction, withMissingNow = true): string[] {
  return [
    ...(summary.uses.length === 0 ? [] : [t("threads.switch.uses", { outputs: namesText(summary.uses, t) })]),
    ...(!withMissingNow || summary.missingNow.length === 0 ? [] : [t("threads.switch.missingNow", { inputs: quoted(summary.missingNow, t) })]),
    ...(summary.neededLater.length === 0 ? [] : [t("threads.switch.neededLater", { inputs: quoted(summary.neededLater, t) })]),
  ];
}

/** How much of an output a choice between outputs shows; its whole summary is its hint. */
const CHOICE_PREVIEW_CHARACTERS = 60;

/** An output among several that fit an input: its kind and step, its entry, and what it gave at a glance (WD-4). */
export function madeChoiceText(made: MadeOutput, t: TFunction): string {
  const glance = valueText(made.value, t);
  const preview = glance.length <= CHOICE_PREVIEW_CHARACTERS ? glance : `${glance.slice(0, CHOICE_PREVIEW_CHARACTERS)}…`;
  return t("threads.switch.madeChoice", { made: madeText(made, t), entry: made.entry, preview });
}

/** Why the switch can't be made yet, in one sentence; `null` when it can. */
export function switchBlocker(summary: SwitchSummary, t: TFunction): string | null {
  const sentences = [
    ...(summary.missingNow.length === 0 ? [] : [t("threads.switch.missingNow", { inputs: quoted(summary.missingNow, t) })]),
    ...summary.problems,
  ];
  return sentences.length === 0 ? null : sentences.map((sentence) => (sentence.endsWith(".") ? sentence : `${sentence}.`)).join(" ");
}

/** The label of a Workflow's step input by its destination: as it exposes it, asks for it, or the step plans it. */
export function inputLabel(workflow: SwitchWorkflow, destination: string): string {
  const step = stepOfDestination(destination);
  const name = destination.slice(step.length + 1);
  return (
    workflow.inputs.find((input) => input.destination === destination)?.label ??
    workflow.asked.find((input) => input.destination === destination)?.label ??
    workflow.steps.find((plan) => plan.stepKey === step)?.inputs.find((input) => input.name === name)?.label ??
    name
  );
}

/** The flat step key of a destination `<flat step key>.<input>`; a flat key may hold dots. */
export function stepOfDestination(destination: string): string {
  const cut = destination.lastIndexOf(".");
  return cut < 0 ? destination : destination.slice(0, cut);
}

function isMissing(reason: DisabledReason): boolean {
  return reason.code === "missing_input" || reason.code === "missing_context";
}

function quoted(labels: readonly string[], t: TFunction): string {
  return namesText(labels.map((label) => t("threads.prepare.run.quoted", { input: label })), t);
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values)];
}
